import type { LucideIcon } from 'lucide-react';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { cssVars, cx } from '@/lib/cx';
import { t } from '@/lib/i18n';
import s from './Badge.module.css';

export type Tone = 'good' | 'medium' | 'bad' | 'info' | 'neutral' | 'accent' | 'caution';

/** UPPERCASE state pill in the book style (Space Grotesk, tracked, tinted pair). */
export function Pill({
  tone = 'neutral',
  children,
  icon: Icon,
  className,
  title,
}: {
  tone?: Tone;
  children: ReactNode;
  icon?: LucideIcon;
  className?: string;
  title?: string;
}) {
  return (
    <span className={cx(s.pill, s[tone], className)} title={title}>
      {Icon && <Icon className={s.pillIcon} aria-hidden />}
      <span className={s.pillLabel}>{children}</span>
    </span>
  );
}

/** Alias: a Badge is a Pill. */
export const Badge = Pill;

/** A count in a small rounded box (sentence case, not a state). */
export function Count({ children, tone = 'neutral' }: { children: ReactNode; tone?: Tone }) {
  return <span className={cx(s.count, s[tone])}>{children}</span>;
}

/** Group chip: colour dot + name. Sentence case — it is a label, not a state. */
export function Chip({
  label,
  color,
  title,
  onRemove,
  className,
  size = 'md',
}: {
  label: ReactNode;
  color?: string | null;
  title?: string;
  onRemove?: () => void;
  className?: string;
  size?: 'sm' | 'md';
}) {
  return (
    <span className={cx(s.chip, s[`chip-${size}`], className)} title={title}>
      <span className={cx(s.dot, !color && s.dotNeutral)} style={cssVars({ '--dot': color ?? undefined })} aria-hidden />
      <span className={s.chipLabel}>{label}</span>
      {onRemove && (
        <button type="button" className={s.remove} onClick={onRemove} aria-label={t('Remove')}>
          <X aria-hidden />
        </button>
      )}
    </span>
  );
}

/** Up to `max` chips, then "+N". */
export function ChipList({
  items,
  max = 3,
  size = 'sm',
}: {
  items: { id: string; label: string; color?: string | null; title?: string }[];
  max?: number;
  size?: 'sm' | 'md';
}) {
  if (!items.length) return <span className={s.none}>—</span>;
  const shown = items.slice(0, max);
  const rest = items.length - shown.length;
  return (
    <span className={s.chipList}>
      {shown.map((i) => (
        <Chip key={i.id} label={i.label} color={i.color} title={i.title} size={size} />
      ))}
      {rest > 0 && (
        <span
          className={s.more}
          title={items
            .slice(max)
            .map((i) => i.label)
            .join(', ')}
        >
          +{rest}
        </span>
      )}
    </span>
  );
}
